import i18next from "i18next";
import * as yup from "yup";

// EN-5191: every message is a thunk, not a string. These schemas are module constants built
// at import time — before the published bundle has been fetched — so a plain i18next.t() call
// would freeze the baseline English into them. yup calls a function message when it builds
// the error, which is late enough to pick up the current language.

export const measurementConditionSchema = yup.object().shape({
  measurementType: yup
    .string()
    .required(() =>
      i18next.t("workflows_rule_validation_measurementTypeRequired")
    ),
  conditionComparatorType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_comparatorRequired")),
  threshold: yup
    .number()
    .moreThan(0, () =>
      i18next.t("workflows_rule_validation_thresholdGreaterThanZero")
    )
    .required(() => i18next.t("workflows_rule_validation_thresholdRequired")),
  triggerType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_ruleTriggerRequired")),
  excludeBadBlows: yup.boolean(),
});

export const surveyConditionSchema = yup.object().shape({
  surveyId: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_surveyRequired")),
  surveyQuestionIds: yup
    .array()
    .of(yup.string())
    .min(1, () => i18next.t("workflows_rule_validation_questionMin"))
    .required(() => i18next.t("workflows_rule_validation_questionsRequired")),
  surveyQuestionOptionIds: yup
    .array()
    .of(yup.string())
    .min(1, () => i18next.t("workflows_rule_validation_answerMin"))
    .required(() => i18next.t("workflows_rule_validation_answersRequired")),
});

export const surveyScoreConditionSchema = yup.object().shape({
  surveyId: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_surveyRequired")),
  conditionComparatorType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_comparatorRequired")),
  surveyDomain: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_domainRequired")),
  threshold: yup
    .number()
    .moreThan(0, () =>
      i18next.t("workflows_rule_validation_thresholdGreaterThanZero")
    )
    .required(() => i18next.t("workflows_rule_validation_thresholdRequired")),
});

export const inactivityConditionSchema = yup.object().shape({
  inactivityDays: yup
    .number()
    .min(1, () => i18next.t("workflows_rule_validation_daysMin"))
    .required(() =>
      i18next.t("workflows_rule_validation_daysOfInactivityRequired")
    ),
  executionTime: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_timeRequired")),
  timezone: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_timezoneRequired")),
});

export const fibricheckStatusConditionSchema = yup.object().shape({
  fibricheckStatus: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_statusRequired")),
});

export const aliveCorEcgStatusConditionSchema = yup.object().shape({
  aliveCorEcgStatus: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_statusRequired")),
});

// EN-5089. The count cap mirrors the API's MaxOccurrenceCount.
export const spirometryStatusConditionSchema = yup.object().shape({
  spirometryStatus: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_statusRequired")),
  occurrenceCount: yup
    .number()
    .typeError(() =>
      i18next.t("workflows_rule_validation_numberOfTimesRequired")
    )
    .integer(() => i18next.t("workflows_rule_validation_numberOfTimesInteger"))
    .min(1, () => i18next.t("workflows_rule_validation_numberOfTimesMin"))
    .max(100, () => i18next.t("workflows_rule_validation_numberOfTimesMax"))
    .required(() =>
      i18next.t("workflows_rule_validation_numberOfTimesRequired")
    ),
  occurrencePeriod: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_periodRequired")),
});

export const sleepConditionSchema = yup.object().shape({
  sleepStage: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_sleepStageRequired")),
  conditionComparatorType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_comparatorRequired")),
  threshold: yup
    .number()
    .moreThan(0, () =>
      i18next.t("workflows_rule_validation_thresholdGreaterThanZero")
    )
    .max(24 * 60, () =>
      i18next.t("workflows_rule_validation_thresholdMax24Hours")
    )
    .required(() => i18next.t("workflows_rule_validation_thresholdRequired")),
  triggerType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_ruleTriggerRequired")),
});

export const activityConditionSchema = yup.object().shape({
  activityMetric: yup
    .string()
    .required(() =>
      i18next.t("workflows_rule_validation_activityMetricRequired")
    ),
  conditionComparatorType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_comparatorRequired")),
  threshold: yup
    .number()
    .moreThan(0, () =>
      i18next.t("workflows_rule_validation_thresholdGreaterThanZero")
    )
    .max(24 * 60, () =>
      i18next.t("workflows_rule_validation_thresholdMax24Hours")
    )
    .required(() => i18next.t("workflows_rule_validation_thresholdRequired")),
});

const SPECIFIC_DATE = "SpecificDate";
const VIDEO_APPOINTMENT = "VideoAppointment";
const MAX_DAYS_OFFSET = 365;

/**
 * EN-5197 Date rule. daysOffset is signed (before < 0, on = 0, after > 0). Only video appointments are
 * known in advance, so every other event must be at least 1 day after; the API enforces the same rules.
 */
export const dateEventConditionSchema = yup.object().shape({
  dateEventType: yup
    .string()
    .required(() => i18next.t("workflows_rule_validation_eventRequired")),
  daysOffset: yup.number().when("dateEventType", {
    is: SPECIFIC_DATE,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .integer(() => i18next.t("workflows_rule_validation_daysInteger"))
        // Only Video appointment keeps this floor: the .min(1, ...) below replaces it for every other event.
        .min(-MAX_DAYS_OFFSET, () =>
          i18next.t("workflows_validation_daysMin", { days: MAX_DAYS_OFFSET })
        )
        .max(MAX_DAYS_OFFSET, () =>
          i18next.t("workflows_validation_daysMax", { days: MAX_DAYS_OFFSET })
        )
        .when("dateEventType", {
          is: (value: string | undefined) =>
            value !== undefined && value !== VIDEO_APPOINTMENT,
          then: (past) =>
            past.min(1, () => i18next.t("workflows_rule_validation_daysMin")),
        })
        .required(() => i18next.t("workflows_rule_validation_daysRequired")),
  }),
  targetDate: yup.string().when("dateEventType", {
    is: SPECIFIC_DATE,
    then: (schema) =>
      schema.required(() =>
        i18next.t("workflows_rule_validation_dateRequired")
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const createRuleValidationSchema = () =>
  yup.object().shape({
    id: yup.string(),
    ruleType: yup
      .string()
      .required(() => i18next.t("workflows_rule_validation_typeRequired")),
    conditions: yup
      .array()
      .when("ruleType", {
        is: "Measurement",
        then: (schema) =>
          schema
            .of(measurementConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "SurveyResponse",
        then: (schema) =>
          schema
            .of(surveyConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "SurveyScore",
        then: (schema) =>
          schema
            .of(surveyScoreConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "Inactivity",
        then: (schema) =>
          schema
            .of(inactivityConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "FibricheckStatus",
        then: (schema) =>
          schema
            .of(fibricheckStatusConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "AliveCorEcgStatus",
        then: (schema) =>
          schema
            .of(aliveCorEcgStatusConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "Sleep",
        then: (schema) =>
          schema
            .of(sleepConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "Activity",
        then: (schema) =>
          schema
            .of(activityConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "SpirometryStatus",
        then: (schema) =>
          schema
            .of(spirometryStatusConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .when("ruleType", {
        is: "DateEvent",
        then: (schema) =>
          schema
            .of(dateEventConditionSchema)
            .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
      })
      .min(1, () => i18next.t("workflows_rule_validation_conditionMin")),
  });
